import { useState, useEffect } from "react";
import { useQuery, useMutation } from "react-query";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Building2, DollarSign, TrendingUp, Save, CreditCard, AlertCircle, Plus, Trash2, Edit2, X } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function GroupInfo() {
  const [enableShareSystem, setEnableShareSystem] = useState(false);
  const [shareValue, setShareValue] = useState("");
  const [minShares, setMinShares] = useState("");
  const [maxShares, setMaxShares] = useState("");
  const [salaryPaymentDay, setSalaryPaymentDay] = useState("");
  const [shareDeclarationDeadlineDay, setShareDeclarationDeadlineDay] = useState("");
  // Loan configuration state
  const [loanInterestRate, setLoanInterestRate] = useState("");
  const [maxLoanMultiplier, setMaxLoanMultiplier] = useState("");
  const [latePaymentPenaltyRate, setLatePaymentPenaltyRate] = useState("");
  const [gracePeriodDays, setGracePeriodDays] = useState("");
  const [groupName, setGroupName] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [schoolAddress, setSchoolAddress] = useState("");
  const [isEditingGroupInfo, setIsEditingGroupInfo] = useState(false);
  
  // Loan types management
  const [loanTypes, setLoanTypes] = useState<any[]>([]);
  const [isAddingLoanType, setIsAddingLoanType] = useState(false);
  const [editingLoanTypeId, setEditingLoanTypeId] = useState<number | null>(null);
  const [newLoanType, setNewLoanType] = useState({
    name: "",
    description: "",
    isActive: true,
    maxAmount: "",
    minAmount: "",
    interestRate: "",
    maxTermMonths: "",
    requiresInsurers: false,
    requiredInsurerCount: "",
    insurerCoverageRatio: "",
    maxLoansPerInsurer: "4",
    defaultTermMonths: "",
  });

  // Fetch group data
  const { data: groupData, isLoading, refetch } = useQuery(
    ["teacherGroup"],
    async () => {
      const { data } = await api.get("/teacher-groups");
      const firstGroup = data.data?.[0];
      
      // If no group exists, create a default one
      if (!firstGroup) {
        try {
          console.log("No saving group found, creating default group...");
          const createResponse = await api.post("/teacher-groups", {
            name: "Default Saving Group",
            schoolName: "School",
            schoolAddress: "",
            isActive: true,
            description: "Default saving group",
          });
          
          if (createResponse.data?.data) {
            toast.success("Default saving group created successfully!");
            return createResponse.data.data;
          }
        } catch (createError: any) {
          console.error("Error creating default saving group:", createError);
          // Don't throw - allow the page to load even if creation fails
        }
      }
      
      return firstGroup; // Get first group
    },
    {
      onSuccess: (data) => {
        if (data) {
          setEnableShareSystem(data.enableShareSystem || false);
          setShareValue(data.shareValue || "");
          setMinShares(data.minSharesPerMonth || "");
          setMaxShares(data.maxSharesPerMonth || "");
          setSalaryPaymentDay(data.salaryPaymentDay || "");
          setShareDeclarationDeadlineDay(data.shareDeclarationDeadlineDay || "");
          // Load loan configuration
          setLoanInterestRate(data.loanInterestRate?.toString() || "");
          setMaxLoanMultiplier(data.maxLoanMultiplier?.toString() || "");
          setLatePaymentPenaltyRate(data.latePaymentPenaltyRate?.toString() || "");
          setGracePeriodDays(data.gracePeriodDays?.toString() || "");
          // Load group info
          setGroupName(data.name || "");
          setSchoolName(data.schoolName || "");
          setSchoolAddress(data.schoolAddress || "");
        }
      },
    }
  );

  // Fetch loan types
  const { data: loanTypesData, refetch: refetchLoanTypes } = useQuery(
    ["loanTypes", groupData?.id],
    async () => {
      if (!groupData?.id) return [];
      const { data } = await api.get(`/loan-types?groupId=${groupData.id}`);
      return data.data || [];
    },
    {
      enabled: !!groupData?.id,
    }
  );

  // Update loanTypes state when data changes
  useEffect(() => {
    if (loanTypesData) {
      setLoanTypes(loanTypesData);
    }
  }, [loanTypesData]);

  // Update group mutation
  const updateGroupMutation = useMutation(
    async (data: any) => {
      // Get group ID - try from current groupData first, then fetch fresh
      let groupId = groupData?.id;
      
      if (!groupId) {
        // Fetch fresh group data to get the ID
        try {
          const { data: freshData } = await api.get("/teacher-groups");
          const firstGroup = freshData?.data?.[0];
          if (firstGroup?.id) {
            groupId = firstGroup.id;
          }
        } catch (fetchError) {
          console.error("Error fetching group data:", fetchError);
        }
      }
      
      // If no group exists, create one automatically
      if (!groupId) {
        try {
          console.log("No saving group found, creating default group...");
          const createPayload = {
            name: groupName || "Default Saving Group",
            schoolName: schoolName || "School",
            schoolAddress: schoolAddress || "",
            isActive: true,
            enableShareSystem: data.enableShareSystem || false,
            shareValue: data.shareValue ? parseInt(data.shareValue) : null,
            minSharesPerMonth: data.minSharesPerMonth ? parseInt(data.minSharesPerMonth) : 1,
            maxSharesPerMonth: data.maxSharesPerMonth ? parseInt(data.maxSharesPerMonth) : null,
            salaryPaymentDay: data.salaryPaymentDay ? parseInt(data.salaryPaymentDay) : 25,
            shareDeclarationDeadlineDay: data.shareDeclarationDeadlineDay ? parseInt(data.shareDeclarationDeadlineDay) : 20,
            meetingFrequency: "Monthly" as const,
          };
          
          const createResponse = await api.post("/teacher-groups", createPayload);
          
          // Handle both success: true and success: false responses
          if (createResponse.data?.success && createResponse.data?.data?.id) {
            groupId = createResponse.data.data.id;
            toast.success("Saving group created successfully!");
            // Refetch to update the UI
            refetch();
          } else if (createResponse.data?.data?.id) {
            // Handle case where response doesn't have success field
            groupId = createResponse.data.data.id;
            toast.success("Saving group created successfully!");
            refetch();
          } else {
            throw new Error(createResponse.data?.message || "Failed to create saving group");
          }
        } catch (createError: any) {
          console.error("Error creating saving group:", createError);
          const errorMessage = createError.response?.data?.message || 
                              createError.response?.data?.error ||
                              createError.message ||
                              "No saving group found and failed to create one. Please contact support.";
          throw new Error(errorMessage);
        }
      }
      
      // Now update the group
      const response = await api.put(`/teacher-groups/${groupId}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Group settings updated successfully!");
        refetch();
      },
      onError: (error: any) => {
        const errorMessage = error.message || error.response?.data?.message || "Failed to update settings";
        toast.error(errorMessage);
        console.error("Update group error:", error);
      },
    }
  );

  const handleSaveShareSystem = () => {
    if (enableShareSystem) {
      // Validate required fields
      if (!shareValue || !minShares || !salaryPaymentDay || !shareDeclarationDeadlineDay) {
        toast.error("Please fill all share system fields");
        return;
      }

      const parsedMax = maxShares ? parseInt(maxShares) : null;
      const parsedMin = parseInt(minShares);

      if (parsedMax !== null && parsedMin > parsedMax) {
        toast.error("Minimum shares cannot be greater than maximum shares");
        return;
      }

      if (parseInt(salaryPaymentDay) < 1 || parseInt(salaryPaymentDay) > 31) {
        toast.error("Salary payment day must be between 1 and 31");
        return;
      }

      if (parseInt(shareDeclarationDeadlineDay) < 1 || parseInt(shareDeclarationDeadlineDay) > 31) {
        toast.error("Declaration deadline day must be between 1 and 31");
        return;
      }
    }

    updateGroupMutation.mutate({
      enableShareSystem,
      shareValue: enableShareSystem ? parseInt(shareValue) : null,
      minSharesPerMonth: enableShareSystem ? parseInt(minShares) : 1,
      maxSharesPerMonth: enableShareSystem && maxShares ? parseInt(maxShares) : null,
      salaryPaymentDay: enableShareSystem ? parseInt(salaryPaymentDay) : 25,
      shareDeclarationDeadlineDay: enableShareSystem ? parseInt(shareDeclarationDeadlineDay) : 20,
    });
  };

  const handleSaveLoanSettings = () => {
    // Validate loan settings
    if (!loanInterestRate || !maxLoanMultiplier || !latePaymentPenaltyRate || !gracePeriodDays) {
      toast.error("Please fill all loan configuration fields");
      return;
    }

    if (parseFloat(loanInterestRate) < 0 || parseFloat(loanInterestRate) > 100) {
      toast.error("Interest rate must be between 0 and 100");
      return;
    }

    if (parseInt(maxLoanMultiplier) < 1) {
      toast.error("Max loan multiplier must be at least 1");
      return;
    }

    if (parseFloat(latePaymentPenaltyRate) < 0 || parseFloat(latePaymentPenaltyRate) > 100) {
      toast.error("Penalty rate must be between 0 and 100");
      return;
    }

    if (parseInt(gracePeriodDays) < 0) {
      toast.error("Grace period days must be 0 or greater");
      return;
    }

    updateGroupMutation.mutate({
      loanInterestRate: parseFloat(loanInterestRate),
      maxLoanMultiplier: parseInt(maxLoanMultiplier),
      latePaymentPenaltyRate: parseFloat(latePaymentPenaltyRate),
      gracePeriodDays: parseInt(gracePeriodDays),
    });
  };

  const handleSaveGroupInfo = () => {
    if (!groupName || !schoolName) {
      toast.error("Group name and school name are required");
      return;
    }

    updateGroupMutation.mutate({
      name: groupName,
      schoolName: schoolName,
      schoolAddress: schoolAddress,
    });
    setIsEditingGroupInfo(false);
  };

  // Loan type management functions
  const handleAddLoanType = async () => {
    if (!newLoanType.name.trim()) {
      toast.error("Loan type name is required");
      return;
    }

    try {
      await api.post("/loan-types", {
        ...newLoanType,
        teacherGroupId: groupData?.id,
        maxAmount: newLoanType.maxAmount ? parseInt(newLoanType.maxAmount) : null,
        minAmount: newLoanType.minAmount ? parseInt(newLoanType.minAmount) : 0,
        interestRate: newLoanType.interestRate ? parseFloat(newLoanType.interestRate) : null,
        maxTermMonths: newLoanType.maxTermMonths ? parseInt(newLoanType.maxTermMonths) : null,
        requiresInsurers: newLoanType.requiresInsurers,
        requiredInsurerCount: newLoanType.requiredInsurerCount
          ? parseInt(newLoanType.requiredInsurerCount)
          : null,
        insurerCoverageRatio: newLoanType.insurerCoverageRatio
          ? parseFloat(newLoanType.insurerCoverageRatio)
          : null,
        maxLoansPerInsurer: newLoanType.maxLoansPerInsurer
          ? parseInt(newLoanType.maxLoansPerInsurer)
          : 4,
        defaultTermMonths: newLoanType.defaultTermMonths
          ? parseInt(newLoanType.defaultTermMonths)
          : null,
      });
      toast.success("Loan type added successfully");
      setIsAddingLoanType(false);
      setNewLoanType({
        name: "",
        description: "",
        isActive: true,
        maxAmount: "",
        minAmount: "",
        interestRate: "",
        maxTermMonths: "",
        requiresInsurers: false,
        requiredInsurerCount: "",
        insurerCoverageRatio: "",
        maxLoansPerInsurer: "4",
        defaultTermMonths: "",
      });
      refetchLoanTypes();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to add loan type");
    }
  };

  const handleUpdateLoanType = async (id: number, updates: any) => {
    try {
      await api.put(`/loan-types/${id}`, updates);
      toast.success("Loan type updated successfully");
      setEditingLoanTypeId(null);
      refetchLoanTypes();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update loan type");
    }
  };

  const handleDeleteLoanType = async (id: number) => {
    if (!confirm("Are you sure you want to delete this loan type?")) {
      return;
    }

    try {
      await api.delete(`/loan-types/${id}`);
      toast.success("Loan type deleted successfully");
      refetchLoanTypes();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete loan type");
    }
  };

  const handleToggleLoanTypeActive = async (loanType: any) => {
    await handleUpdateLoanType(loanType.id, {
      isActive: !loanType.isActive,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4dbd07]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-theme-text">
            Group Settings
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
            Manage your savings group configuration and share system
          </p>
        </div>
      </div>

      {/* Group Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Group Name</CardTitle>
            <div className="flex items-center gap-2">
              {!isEditingGroupInfo && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingGroupInfo(true)}
                  className="h-6 w-6 p-0"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </Button>
              )}
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            {isEditingGroupInfo ? (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="groupName">Group Name *</Label>
                  <Input
                    id="groupName"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Enter group name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="schoolName">School Name *</Label>
                  <Input
                    id="schoolName"
                    value={schoolName}
                    onChange={(e) => setSchoolName(e.target.value)}
                    placeholder="Enter school name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="schoolAddress">School Address</Label>
                  <Input
                    id="schoolAddress"
                    value={schoolAddress}
                    onChange={(e) => setSchoolAddress(e.target.value)}
                    placeholder="Enter school address"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveGroupInfo}
                    disabled={updateGroupMutation.isLoading}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setIsEditingGroupInfo(false);
                      setGroupName(groupData?.name || "");
                      setSchoolName(groupData?.schoolName || "");
                      setSchoolAddress(groupData?.schoolAddress || "");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-base sm:text-lg md:text-2xl font-bold" style={{ color: '#4dbd07' }}>
                  {groupData?.name || "N/A"}
                </div>
                <p className="text-xs text-muted-foreground">
                  {groupData?.schoolName || "School name"}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Share System</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-base sm:text-lg md:text-2xl font-bold" style={{ color: groupData?.enableShareSystem ? '#4dbd07' : '#6b7280' }}>
              {groupData?.enableShareSystem ? "Enabled" : "Disabled"}
            </div>
            <p className="text-xs text-muted-foreground">
              {groupData?.enableShareSystem ? "Share-based contributions active" : "Traditional contributions"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Share Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-base sm:text-lg md:text-2xl font-bold" style={{ color: '#4dbd07' }}>
              {groupData?.shareValue?.toLocaleString() || "N/A"} FRW
            </div>
            <p className="text-xs text-muted-foreground">
              Per share
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Share System Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Share-Based Contribution System
          </CardTitle>
          <CardDescription>
            Configure the share-based contribution system for automated salary deductions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="enable-shares" className="text-base font-semibold">
                Enable Share System
              </Label>
              <div className="text-sm text-muted-foreground">
                Activate share-based contributions for your group
              </div>
            </div>
            <Switch
              id="enable-shares"
              checked={enableShareSystem}
              onCheckedChange={setEnableShareSystem}
              className="h-7 w-14 border border-gray-300 bg-gray-300 shadow-inner data-[state=checked]:border-[#4dbd07] data-[state=checked]:bg-[#4dbd07] [&>span]:h-6 [&>span]:w-6 [&>span]:bg-white [&>span]:shadow-md [&>span]:data-[state=checked]:translate-x-7"
            />
          </div>

          {enableShareSystem && (
            <>
              <Separator />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* Share Value */}
                <div className="space-y-2">
                  <Label htmlFor="shareValue">
                    Share Value (FRW) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="shareValue"
                    type="number"
                    placeholder="e.g., 10000"
                    value={shareValue}
                    onChange={(e) => setShareValue(e.target.value)}
                    min="1"
                  />
                  <p className="text-xs text-muted-foreground">
                    The monetary value of one share
                  </p>
                </div>

                {/* Min Shares */}
                <div className="space-y-2">
                  <Label htmlFor="minShares">
                    Minimum Shares Per Month <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="minShares"
                    type="number"
                    placeholder="e.g., 1"
                    value={minShares}
                    onChange={(e) => setMinShares(e.target.value)}
                    min="1"
                  />
                  <p className="text-xs text-muted-foreground">
                    Minimum shares a member must contribute
                  </p>
                </div>

                {/* Max Shares */}
                <div className="space-y-2">
                  <Label htmlFor="maxShares">
                    Maximum Shares Per Month (optional)
                  </Label>
                  <Input
                    id="maxShares"
                    type="number"
                    placeholder="e.g., 5"
                    value={maxShares}
                    onChange={(e) => setMaxShares(e.target.value)}
                    min="1"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank if members can buy unlimited shares
                  </p>
                </div>

                {/* Salary Payment Day */}
                <div className="space-y-2">
                  <Label htmlFor="salaryPaymentDay">
                    Salary Payment Day <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="salaryPaymentDay"
                    type="number"
                    placeholder="e.g., 25"
                    value={salaryPaymentDay}
                    onChange={(e) => setSalaryPaymentDay(e.target.value)}
                    min="1"
                    max="31"
                  />
                  <p className="text-xs text-muted-foreground">
                    Day of month when salaries are paid (1-31)
                  </p>
                </div>

                {/* Declaration Deadline Day */}
                <div className="space-y-2">
                  <Label htmlFor="deadlineDay">
                    Declaration Deadline Day <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="deadlineDay"
                    type="number"
                    placeholder="e.g., 20"
                    value={shareDeclarationDeadlineDay}
                    onChange={(e) => setShareDeclarationDeadlineDay(e.target.value)}
                    min="1"
                    max="31"
                  />
                  <p className="text-xs text-muted-foreground">
                    Deadline day for members to declare shares (1-31)
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
            >
              Cancel
            </Button>
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <Button
                onClick={handleSaveShareSystem}
                disabled={updateGroupMutation.isLoading}
                className="bg-[#4dbd07] text-white hover:bg-[#3d9a06] hover:text-white disabled:bg-[#4dbd07]/60"
              >
                {updateGroupMutation.isLoading ? (
                  <>Saving...</>
                ) : (
                  <>Save Share System Settings</>
                )}
              </Button>
            </motion.div>
          </div>
        </CardContent>
      </Card>

      {/* Loan Types Management */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
                <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
                Loan Types Management
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm mt-1">
                Create and manage custom loan types for your group (e.g., Emergency, Business, Education)
              </CardDescription>
            </div>
            {!isAddingLoanType && (
              <Button
                onClick={() => setIsAddingLoanType(true)}
                size="sm"
                className="bg-[#4dbd07] text-white hover:bg-[#3d9a06] w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Loan Type
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-6">
          {/* Add New Loan Type Form */}
          {isAddingLoanType && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold">Add New Loan Type</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsAddingLoanType(false);
                      setNewLoanType({
                        name: "",
                        description: "",
                        isActive: true,
                        maxAmount: "",
                        minAmount: "",
                        interestRate: "",
                        maxTermMonths: "",
                        requiresInsurers: false,
                        requiredInsurerCount: "",
                        insurerCoverageRatio: "",
                        maxLoansPerInsurer: "4",
                        defaultTermMonths: "",
                      });
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Loan Type Name *</Label>
                    <Input
                      placeholder="e.g., Emergency, Business, Education"
                      value={newLoanType.name}
                      onChange={(e) => setNewLoanType({ ...newLoanType, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      placeholder="Optional description"
                      value={newLoanType.description}
                      onChange={(e) => setNewLoanType({ ...newLoanType, description: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Min Amount (FRW)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 0"
                      value={newLoanType.minAmount}
                      onChange={(e) => setNewLoanType({ ...newLoanType, minAmount: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Amount (FRW)</Label>
                    <Input
                      type="number"
                      placeholder="Optional maximum amount"
                      value={newLoanType.maxAmount}
                      onChange={(e) => setNewLoanType({ ...newLoanType, maxAmount: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Interest Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Optional specific rate"
                      value={newLoanType.interestRate}
                      onChange={(e) => setNewLoanType({ ...newLoanType, interestRate: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Repayment Term (Months)</Label>
                    <Input
                      type="number"
                      placeholder="Default repayment term"
                      value={newLoanType.defaultTermMonths}
                      onChange={(e) =>
                        setNewLoanType({ ...newLoanType, defaultTermMonths: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Term (Months)</Label>
                    <Input
                      type="number"
                      placeholder="Optional maximum term"
                      value={newLoanType.maxTermMonths}
                      onChange={(e) => setNewLoanType({ ...newLoanType, maxTermMonths: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2 flex items-end">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newLoanType.requiresInsurers}
                        onCheckedChange={(checked) =>
                          setNewLoanType({ ...newLoanType, requiresInsurers: checked })
                        }
                      />
                      <Label>Requires Insurers</Label>
                    </div>
                  </div>
                  {newLoanType.requiresInsurers && (
                    <>
                      <div className="space-y-2">
                        <Label>Required Insurer Count</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 3"
                          value={newLoanType.requiredInsurerCount}
                          onChange={(e) =>
                            setNewLoanType({ ...newLoanType, requiredInsurerCount: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Coverage per Insurer (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="e.g., 33.33"
                          value={newLoanType.insurerCoverageRatio}
                          onChange={(e) =>
                            setNewLoanType({ ...newLoanType, insurerCoverageRatio: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Max Loans per Insurer</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 4"
                          value={newLoanType.maxLoansPerInsurer}
                          onChange={(e) =>
                            setNewLoanType({ ...newLoanType, maxLoansPerInsurer: e.target.value })
                          }
                        />
                      </div>
                    </>
                  )}
                  <div className="space-y-2 flex items-end">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newLoanType.isActive}
                        onCheckedChange={(checked) => setNewLoanType({ ...newLoanType, isActive: checked })}
                      />
                      <Label>Active</Label>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={handleAddLoanType}
                    className="bg-[#4dbd07] text-white hover:bg-[#3d9a06] w-full sm:w-auto"
                  >
                    Add Loan Type
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsAddingLoanType(false);
                      setNewLoanType({
                        name: "",
                        description: "",
                        isActive: true,
                        maxAmount: "",
                        minAmount: "",
                        interestRate: "",
                        maxTermMonths: "",
                        requiresInsurers: false,
                        requiredInsurerCount: "",
                        insurerCoverageRatio: "",
                        maxLoansPerInsurer: "4",
                        defaultTermMonths: "",
                      });
                    }}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loan Types List */}
          <div className="space-y-2">
            {loanTypes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No loan types configured. Click "Add Loan Type" to create one.
              </div>
            ) : (
              loanTypes.map((loanType) => (
                <Card key={loanType.id} className={!loanType.isActive ? "opacity-60" : ""}>
                  <CardContent className="pt-4 p-4 sm:p-6">
                    {editingLoanTypeId === loanType.id ? (
                      <LoanTypeEditForm
                        loanType={loanType}
                        onSave={(updates) => handleUpdateLoanType(loanType.id, updates)}
                        onCancel={() => setEditingLoanTypeId(null)}
                      />
                    ) : (
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-sm sm:text-base md:text-lg text-black break-words">{loanType.name}</h4>
                            {!loanType.isActive && (
                              <Badge variant="secondary" className="text-xs">Inactive</Badge>
                            )}
                          </div>
                          {loanType.description && (
                            <p className="text-xs sm:text-sm text-black mt-1 break-words">{loanType.description}</p>
                          )}
                          <div className="flex flex-wrap gap-2 sm:gap-4 mt-2 text-xs text-black">
                            {loanType.maxAmount && (
                              <span className="whitespace-nowrap">Max: {loanType.maxAmount.toLocaleString()} FRW</span>
                            )}
                            <span className="whitespace-nowrap">Min: {(loanType.minAmount || 0).toLocaleString()} FRW</span>
                            {loanType.interestRate && (
                              <span className="whitespace-nowrap">Rate: {loanType.interestRate}%</span>
                            )}
                            {loanType.maxTermMonths && (
                              <span className="whitespace-nowrap">Max Term: {loanType.maxTermMonths} months</span>
                            )}
                            {loanType.defaultTermMonths && (
                              <span className="whitespace-nowrap">Default Term: {loanType.defaultTermMonths} months</span>
                            )}
                            {loanType.requiresInsurers && (
                              <span className="whitespace-nowrap break-words">
                                {loanType.requiredInsurerCount || 0} insurer(s), {loanType.insurerCoverageRatio || (loanType.requiredInsurerCount ? (100 / loanType.requiredInsurerCount).toFixed(2) : 0)}% each
                              </span>
                            )}
                          </div>
                          {loanType.requiresInsurers && (
                            <p className="text-xs text-black mt-1">
                              Each insurer can cover up to {loanType.maxLoansPerInsurer || 4} loans.
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 self-start sm:self-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleLoanTypeActive(loanType)}
                            className="text-xs sm:text-sm hidden sm:inline-flex"
                          >
                            {loanType.isActive ? "Deactivate" : "Activate"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingLoanTypeId(loanType.id)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLoanType(loanType.id)}
                            className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Loan Type Edit Form Component
function LoanTypeEditForm({ loanType, onSave, onCancel }: any) {
  const [formData, setFormData] = useState({
    name: loanType.name || "",
    description: loanType.description || "",
    isActive: loanType.isActive ?? true,
    maxAmount: loanType.maxAmount?.toString() || "",
    minAmount: loanType.minAmount?.toString() || "",
    interestRate: loanType.interestRate?.toString() || "",
    maxTermMonths: loanType.maxTermMonths?.toString() || "",
    requiresInsurers: loanType.requiresInsurers ?? false,
    requiredInsurerCount: loanType.requiredInsurerCount?.toString() || "",
    insurerCoverageRatio: loanType.insurerCoverageRatio?.toString() || "",
    maxLoansPerInsurer: loanType.maxLoansPerInsurer?.toString() || "4",
    defaultTermMonths: loanType.defaultTermMonths?.toString() || "",
  });

  const handleSave = () => {
    if (!formData.name.trim()) {
      toast.error("Loan type name is required");
      return;
    }

    onSave({
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      isActive: formData.isActive,
      maxAmount: formData.maxAmount ? parseInt(formData.maxAmount) : null,
      minAmount: formData.minAmount ? parseInt(formData.minAmount) : 0,
      interestRate: formData.interestRate ? parseFloat(formData.interestRate) : null,
      maxTermMonths: formData.maxTermMonths ? parseInt(formData.maxTermMonths) : null,
      requiresInsurers: formData.requiresInsurers,
      requiredInsurerCount: formData.requiredInsurerCount
        ? parseInt(formData.requiredInsurerCount)
        : null,
      insurerCoverageRatio: formData.insurerCoverageRatio
        ? parseFloat(formData.insurerCoverageRatio)
        : null,
      maxLoansPerInsurer: formData.maxLoansPerInsurer
        ? parseInt(formData.maxLoansPerInsurer)
        : 4,
      defaultTermMonths: formData.defaultTermMonths
        ? parseInt(formData.defaultTermMonths)
        : null,
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Loan Type Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Min Amount (FRW)</Label>
          <Input
            type="number"
            value={formData.minAmount}
            onChange={(e) => setFormData({ ...formData, minAmount: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Max Amount (FRW)</Label>
          <Input
            type="number"
            value={formData.maxAmount}
            onChange={(e) => setFormData({ ...formData, maxAmount: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Interest Rate (%)</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.interestRate}
            onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Repayment Term (Months)</Label>
          <Input
            type="number"
            value={formData.defaultTermMonths}
            onChange={(e) => setFormData({ ...formData, defaultTermMonths: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Max Term (Months)</Label>
          <Input
            type="number"
            value={formData.maxTermMonths}
            onChange={(e) => setFormData({ ...formData, maxTermMonths: e.target.value })}
          />
        </div>
        <div className="space-y-2 flex items-end">
          <div className="flex items-center space-x-2">
            <Switch
              checked={formData.requiresInsurers}
              onCheckedChange={(checked) => setFormData({ ...formData, requiresInsurers: checked })}
            />
            <Label>Requires Insurers</Label>
          </div>
        </div>
        {formData.requiresInsurers && (
          <>
            <div className="space-y-2">
              <Label>Required Insurer Count</Label>
              <Input
                type="number"
                value={formData.requiredInsurerCount}
                onChange={(e) => setFormData({ ...formData, requiredInsurerCount: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Coverage per Insurer (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.insurerCoverageRatio}
                onChange={(e) => setFormData({ ...formData, insurerCoverageRatio: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Max Loans per Insurer</Label>
              <Input
                type="number"
                value={formData.maxLoansPerInsurer}
                onChange={(e) => setFormData({ ...formData, maxLoansPerInsurer: e.target.value })}
              />
            </div>
          </>
        )}
        <div className="space-y-2 flex items-end">
          <div className="flex items-center space-x-2">
            <Switch
              checked={formData.isActive}
              onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
            />
            <Label>Active</Label>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button onClick={handleSave} size="sm" className="bg-theme-primary text-white w-full sm:w-auto">
          Save
        </Button>
        <Button onClick={onCancel} variant="outline" size="sm" className="w-full sm:w-auto">
          Cancel
        </Button>
      </div>
    </div>
  );
}
